import { createSelector } from "reselect";
import get from "lodash/get";
import filter from "lodash/filter";
import map from "lodash/map";
import includes from "lodash/includes";
import sumBy from "lodash/sumBy";
import max from "lodash/max";
import ceil from "lodash/ceil";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import {
  formatCurrency,
  calculatePayMonthlyItems,
  calculatePayTodayItems,
  formatAddonsForPayMonthly,
  formatMessage,
  urlParam,
  unionMap
} from "utils/helpers";
import {
  currencyFormatSelector,
  deliveryInfoSelector,
  personalDetailsSelector,
  selectedNumberSelector,
  selectedNumberTypeSelector,
  portInSelector,
  numberFormatSelector,
  localeSelector,
  phonesFormSelector,
  selfActivationSelector,
  genericSelector,
  selectedSANumberTypeSelector,
  multiSimOrderSelector,
  deliveryHomeSlotTypeSelector,
  PreCheckoutFormSelector,
  show5GAddonSelector,
  selectAppSettings,
  show5GDeviceCompatibilityCheckerSelector
} from "ducks/selectors";
import {
  referralCodeSelector,
  referralCodeAddonsSelector
} from "./referralCode";
import { PREPAID_PLAN_SUB_TYPES, SIM_TYPES } from "utils/constants";
import { getDefaultAddons } from "utils/localStorage";
import { upgradeTo5GSelector } from "./forms/upgradeTo5G";

export const selectPlanService = state =>
  get(state.api, "planService.data", {});

export const planServiceSelector = selectPlanService;

export const selectSelectedAddons = state =>
  get(state.api, "selectedAddons.data", {});

export const preSelectedOrderAddonsSelector = state =>
  get(state.api, "validateIccId.data.selected_paid_addons", []);

export const selectShowSectionListModal = state =>
  get(state.api, "showSectionListModal.data.enabled", false);

/**
 * 
 * Enterprise Automated REDUX Selector START #####################
 */
export const simCountSelector = state =>
  get(state.api, "simCount.data", {});

export const enableThingsToKnowSelector = state =>
  get(state.api, "enableThingsToKnow.data", false);

export const enableSimCountNextBtnSelector = state =>
  get(state.api, "enableSimCountNextBtn.data", false);

export const isAcraFileUploadSelector = state => 
  get(state.api, "isAcraFileUpload.data", false);

export const companyDetailsSelector = state => 
  get(state.api, "companyDetails.data", {});

export const isDeclaredCompanyDetailsSelector = state => 
  get(state.api, "isDeclaredCompanyDetails.data", false);

export const accountPrimaryDetailsSelector = state => 
  get(state.api, "accountPrimaryDetails.data", {});

export const isPrimaryDetailsCompletedSelector = state => 
  get(state.api, "isPrimaryDetailsCompleted.data", false);

export const accountSecondaryDetailsSelector = state => 
  get(state.api, "accountSecondaryDetails.data", {});

export const isSecondaryDetailsCompletedSelector = state => 
  get(state.api, "isSecondaryDetailsCompleted.data", false);

export const accountBillingDetailsSelector = state => 
  get(state.api, "accountBillingDetails.data", {});

export const isBillingEmailCompletedSelector = state => 
  get(state.api, "isBillingEmailCompleted.data", false);

export const companyVerificationSelector = state => 
  get(state.api, "companyVerification.data", false);

export const isTermsAndConditionAcceptSelector = state => 
  get(state.api, "isTermsAndConditionAccept.data", false);

export const auditLogDataSelector = state => 
  get(state.api, "auditLogData.data", {});
/*
 * 
 * Enterprise Automated REDUX Selector END ########################
 */

// ***************************************************************
// ******************** planService selectors ********************
// ***************************************************************

export const basicPriceSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "basicPrice") || 0
);

export const basicTaxSelector = createSelector(
  planServiceSelector,
  genericSelector,
  (planService, generic) =>
    get(planService, "tax_percentage") || get(generic, "tax_percentage") || 0
);

export const multiSimSelector = urlParam("ms");

export const basicDataSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "data") || 0
);

export const enabledAddonTypesSelector = createSelector(
  genericSelector,
  generic => get(generic, "enabledAddonTypes", [])
);

export const preSelectedAddonsSelector = createSelector(
  referralCodeAddonsSelector,
  referralCodeAddons => {
    const defaultAddons = getDefaultAddons();
    const referralCodeAddonNames = map(referralCodeAddons, "name");
    return [...defaultAddons, ...referralCodeAddonNames];
  }
);

export const planAddonsSelector = createSelector(
  planServiceSelector,
  enabledAddonTypesSelector,
  (planService, enabledAddonTypes) => {
    if (isArray(get(planService, "allowed_products"))) {
      return filter(get(planService, "additional_products"), product =>
        includes(get(planService, "allowed_products"), product.id)
      );
    } else if (!isEmpty(enabledAddonTypes)) {
      // Only the Additional Product types that are in the enabledAddonTypes should be included
      return filter(get(planService, "additional_products"), product =>
        includes(enabledAddonTypes, product.type)
      );
    }

    return get(planService, "additional_products");
  }
);

export const defaultPlanAddonsSelector = createSelector(
  planServiceSelector,
  planService =>
    filter(get(planService, "default_products", []), addon =>
      get(addon.specs, "visible")
    )
);

export const defaultInvisiblePlanAddonsSelector = createSelector(
  planServiceSelector,
  planService =>
    filter(
      get(planService, "default_products", []),
      addon => !get(addon.specs, "visible")
    )
);

export const payMonthlySelector = createSelector(
  planServiceSelector,
  planService =>
    get(
      planService,
      "specs.pay_monthly_override",
      get(planService, "pay_monthly", [])
    )
);

export const payTodaySelector = createSelector(
  planServiceSelector,
  planService => get(planService, "pay_today", [])
);

export const planSubTypeSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "sub_type", "")
);

export const addonsSelector = createSelector(
  currencyFormatSelector,
  planAddonsSelector,
  selectSelectedAddons,
  basicTaxSelector,
  genericSelector,
  (currencyFormat, planAddons, selectedAddons, basicTax, generic) => {
    let addons = get(generic, "show_only_actionable_addons")
      ? filter(planAddons, addon => addon.action)
      : planAddons;
    return formatAddonsForPayMonthly(
      addons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const defaultAddonsSelector = createSelector(
  currencyFormatSelector,
  defaultPlanAddonsSelector,
  basicTaxSelector,
  (currencyFormat, defaultAddons, basicTax) => {
    const selectedAddons = {};
    // all default addons are selected by default
    for (const addon of defaultAddons) {
      selectedAddons[addon.id] = true;
    }
    return formatAddonsForPayMonthly(
      defaultAddons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const defaultInvisibleAddonsSelector = createSelector(
  currencyFormatSelector,
  defaultInvisiblePlanAddonsSelector,
  basicTaxSelector,
  (currencyFormat, defaultInvisibleAddons, basicTax) => {
    const selectedAddons = {};
    // all default addons are selected by default
    for (const addon of defaultInvisibleAddons) {
      selectedAddons[addon.id] = true;
    }
    return formatAddonsForPayMonthly(
      defaultInvisibleAddons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const selectedDeviceSelector = createSelector(
  phonesFormSelector,
  ({ phoneVariant, phonePaymentOption, paymentTerm }) => {
    if (phoneVariant) {
      return {
        label: phoneVariant.nameWithSpecs || phoneVariant.title,
        paymentType: phonePaymentOption,
        paymentTerm: paymentTerm,
        paymentOption: phoneVariant.paymentOption,
        deviceSku: phoneVariant.id
      };
    }
    return null;
  }
);

// ***************************************************************
// ******************* planService makeSelect ********************
// ***************************************************************
export const makeSelectShowSectionListModal = () => selectShowSectionListModal;

export const makeSelectPayMonthlyItems = () =>
  createSelector(
    basicPriceSelector,
    basicTaxSelector,
    payMonthlySelector,
    currencyFormatSelector,
    referralCodeSelector,
    defaultPlanAddonsSelector,
    planSubTypeSelector,
    basicDataSelector,
    phonesFormSelector,
    genericSelector,
    selectedSANumberTypeSelector,
    planServiceSelector,
    additionalAddonsSelector,
    selectedNumberTypeSelector,
    selfActivationSelector,
    genericSelector,
    preSelectedAddonsSelector,
    makeSelectSimCountNewAndTransfer(),
    (
      basicPrice,
      basicTax,
      monthlyItems,
      currencyFormat,
      referralCode,
      defaultPlanAddons,
      planSubType,
      basicData,
      phonesForm,
      genericData,
      numberType,
      planDetails,
      addons,
      selectedNumberType,
      selfActivation,
      generic,
      preSelectedAddons,
      simCounts
    ) => {
      const precision = get(currencyFormat, "precision", 2);
      let payMonthlyItems = includes(PREPAID_PLAN_SUB_TYPES, planSubType)
        ? []
        : calculatePayMonthlyItems({
            basicPrice,
            basicTax,
            items: monthlyItems,
            currencyFormat,
            referralCode,
            addons,
            defaultPlanAddons,
            phonesForm,
            discount: get(genericData, "transfer_number_discount", {}),
            planDetails,
            numberType,
            selectedNumberType,
            selfActivation,
            generic
          });

      const originalPrice = max([
        sumBy(payMonthlyItems, ({ value }) => max([value, 0])),
        0
      ]);
      const originalFixedPrice = formatCurrency(
        originalPrice,
        currencyFormat,
        false,
        0
      );
      const originalPriceWithoutTax = ceil(
        max([
          sumBy(payMonthlyItems, ({ value_without_tax }) =>
            max([value_without_tax, 0])
          ),
          0
        ]),
        precision
      );
      const originalFixedPriceWithoutTax = formatCurrency(
        originalPriceWithoutTax,
        currencyFormat,
        false,
        0
      );
      let totalAmount = max([sumBy(payMonthlyItems, "value"), 0]);
      let totalEnterpriseAmount = max([sumBy(payMonthlyItems, "value"), 0]);
      /***
       * Enterprise  Automated Flow, Price need to be changed according to the SIM count
       * Start
       */
      const {totalSimCount} = simCounts
      if(totalSimCount >= 1){
        totalEnterpriseAmount = totalAmount * totalSimCount
      }
       /***
       * Enterprise  Automated Flow, Price need to be changed according to the SIM count
       * End 
       */
      const totalAmountWithoutTax = ceil(
        max([sumBy(payMonthlyItems, "value_without_tax"), 0]),
        precision
      );
      const totalTaxAmount = ceil(
        totalAmount - totalAmountWithoutTax,
        precision
      );
      let totalData = max([sumBy(payMonthlyItems, "data"), 0]) + basicData;
      let totalEntepriseData = 0;
      if(totalSimCount > 1){
          totalEntepriseData = totalData * totalSimCount;
      }
      const totalRoamingData = max([sumBy(payMonthlyItems, "roaming_data"), 0]);
      const basicMins = get(planDetails, "minutes") || 0;
      const totalMins = max([sumBy(payMonthlyItems, "mins"), 0]) + basicMins;

      // for borderless countries network
      const selectedBorderlessAddons = payMonthlyItems.filter(a =>
        a?.name?.toLowerCase()?.includes("borderless")
      );

      const defaultBorderlessAddons = filter(preSelectedAddons, a =>
        a?.toLowerCase()?.includes("borderless")
      );

      const isBorderlessPlan =
        !isEmpty(selectedBorderlessAddons) || !isEmpty(defaultBorderlessAddons);

      let planCountries = isBorderlessPlan
        ? unionMap(selectedBorderlessAddons, "supportedCountries")
        : [];

      if (isBorderlessPlan) {
        payMonthlyItems = payMonthlyItems.filter(
          a => !a.name?.toLowerCase().includes("borderless")
        );
      }

      return {
        items: payMonthlyItems,
        originalPrice: formatCurrency(originalPrice, currencyFormat),
        originalFixedPrice: formatCurrency(originalFixedPrice, currencyFormat),
        originalPriceWithoutTax: formatCurrency(
          originalPriceWithoutTax,
          currencyFormat
        ),
        originalFixedPriceWithoutTax,
        totalPrice: formatCurrency(totalAmount, currencyFormat),
        totalPriceWithoutTax: formatCurrency(
          totalAmountWithoutTax,
          currencyFormat
        ),
        totalTaxPrice: formatCurrency(totalTaxAmount, currencyFormat),
        totalFixedPrice: formatCurrency(totalAmount, currencyFormat, false, 0),
        totalFixedPriceWithoutTax: formatCurrency(
          totalAmountWithoutTax,
          currencyFormat,
          false,
          0
        ),
        totalEnterpriseAmount: formatCurrency(totalEnterpriseAmount, currencyFormat),
        totalEntepriseData:totalEntepriseData,
        totalData,
        totalRoamingData,
        totalAmount,
        totalAmountWithoutTax,
        totalTaxAmount,
        planCountries,
        isBorderlessPlan,
        selectedBorderlessAddons,
        totalMins
      };
    }
  );

export const makeSelectPayTodayItems = page =>
  createSelector(
    payTodaySelector,
    payMonthlySelector,
    currencyFormatSelector,
    referralCodeSelector,
    deliveryInfoSelector,
    personalDetailsSelector,
    selectedNumberSelector,
    selectedNumberTypeSelector,
    portInSelector,
    basicPriceSelector,
    basicTaxSelector,
    addonsSelector,
    defaultPlanAddonsSelector,
    planSubTypeSelector,
    numberFormatSelector,
    localeSelector,
    phonesFormSelector,
    genericSelector,
    selectedSANumberTypeSelector,
    selfActivationSelector,
    preSelectedOrderAddonsSelector,
    deliveryHomeSlotTypeSelector,
    (
      todayItems,
      monthlyItems,
      currencyFormat,
      referralCode,
      deliveryInfo,
      personalDetails,
      selectedNumber,
      selectedNumberType,
      portInForm,
      basicPrice,
      basicTax,
      addons,
      defaultPlanAddons,
      planSubType,
      numberFormat,
      locale,
      phonesForm,
      genericData,
      numberType,
      selfActivationForm,
      preActivationDeliveryAddon,
      deliveryHomeSlotType
    ) => {
      const payMonthlyItems = !includes(PREPAID_PLAN_SUB_TYPES, planSubType)
        ? []
        : calculatePayMonthlyItems({
            basicPrice,
            basicTax,
            items: monthlyItems,
            currencyFormat,
            referralCode,
            addons,
            defaultPlanAddons,
            phonesForm,
            discount: get(genericData, "transfer_number_discount", {}),
            numberType
          });

      const payTodayItems = calculatePayTodayItems({
        items: todayItems,
        currencyFormat,
        referralCode,
        deliveryInfo,
        personalDetails,
        selectedNumber,
        selectedNumberType,
        portInForm,
        page,
        numberFormat,
        locale,
        phonesForm,
        basicTax,
        selfActivationForm,
        preActivationDeliveryAddon,
        deliveryHomeSlotType
      });

      const allPayTodayItems = [...payMonthlyItems, ...payTodayItems];

      const totalAmount = max([sumBy(allPayTodayItems, "value"), 0]);

      return {
        items: allPayTodayItems,
        totalPrice: formatCurrency(totalAmount, currencyFormat),
        totalFixedPrice: formatCurrency(totalAmount, currencyFormat, false, 0),
        totalAmount
      };
    }
  );

export const additionalAddonsSelector = createSelector(
  addonsSelector,
  selfActivationSelector,
  currencyFormatSelector,
  genericSelector,
  multiSimOrderSelector,
  ([...addOns], selfActivation, currencyFormat, generic, multiSim) => {
    const multi_sim = get(selfActivation, "multi_sim", false);
    const sim_type = get(selfActivation, "sim_type");
    if ((multiSim || multi_sim) && sim_type !== SIM_TYPES.ESIM) {
      const multi_sim_price = get(generic, "multi_sim.price");
      addOns.unshift({
        label: `${get(generic, "multi_sim.label")}`,
        value: multi_sim_price,
        selected: true,
        type: "sim",
        price: formatCurrency(multi_sim_price, currencyFormat, true)
      });
    }
    return addOns;
  }
);

export const is5GBundleAddonSelectedSelector = createSelector(
  addonsSelector,
  planServiceSelector,
  genericSelector,
  (addOns, planService, generic) => {
    const bundle5gAddonSKUs = get(generic, "5g_bundle_addon_skus", []);
    const is5gBundleAddonSelected = addOns.some(
      item => item?.selected && bundle5gAddonSKUs.includes(item.id)
    );
    const is5gBundleAddonDefault = get(
      planService,
      "default_products",
      []
    ).some(item => bundle5gAddonSKUs.includes(item.id));
    return is5gBundleAddonSelected || is5gBundleAddonDefault;
  }
);

export const preselected5GAddonSelector = createSelector(
  preSelectedAddonsSelector,
  is5GBundleAddonSelectedSelector,
  (preSelectedAddons, is5GBundleAddonSelected) => {
    return preSelectedAddons.includes("5G") || is5GBundleAddonSelected;
  }
);

export const is5GAddonSelectedSelector = createSelector(
  addonsSelector,
  planServiceSelector,
  genericSelector,
  (addOns, planService, generic) => {
    const addon5gSKUs = get(generic, "5g_addon_skus", []);
    const is5GAddonSelected = addOns.some(
      item => item?.selected && addon5gSKUs.includes(item.id)
    );
    const is5gBundleAddonDefault = get(
      planService,
      "default_products",
      []
    ).some(item => addon5gSKUs.includes(item.id));
    return is5GAddonSelected || is5gBundleAddonDefault;
  }
);

export const isSimTypeEsimSelector = createSelector(
  selfActivationSelector,
  PreCheckoutFormSelector,
  (selfActivation, preCheckoutForm) => {
    const simType = get(
      selfActivation,
      "sim_type",
      get(preCheckoutForm, "sim_type")
    );
    return simType === "esim";
  }
);

export const selectedMobileDeviceBrandSelector = createSelector(
  selfActivationSelector,
  PreCheckoutFormSelector,
  upgradeTo5GSelector,
  (selfActivation, preCheckoutForm, upgradeTo5GForm) => {
    return get(
      selfActivation,
      "select_mobile_device_brand.value",
      get(
        preCheckoutForm,
        "select_mobile_device_brand.value",
        get(upgradeTo5GForm, "select_mobile_device_brand.value")
      )
    );
  }
);
export const selectedMobileDeviceModelSelector = createSelector(
  selfActivationSelector,
  PreCheckoutFormSelector,
  upgradeTo5GSelector,
  (selfActivation, preCheckoutForm, upgradeTo5GForm) => {
    return get(
      selfActivation,
      "select_mobile_device_model.value",
      get(
        preCheckoutForm,
        "select_mobile_device_model.value",
        get(upgradeTo5GForm, "select_mobile_device_model.value")
      )
    );
  }
);

export const filteredMobileDeviceModelsSelector = createSelector(
  selectedMobileDeviceBrandSelector,
  genericSelector,
  (selectedMobileDeviceBrand, generic) => {
    const compatibleDevices = get(generic, "compatible_devices", []);
    const filteredModels = compatibleDevices.filter(
      device => device?.brand === selectedMobileDeviceBrand
    );
    return !isEmpty(filteredModels) ? filteredModels[0] : [];
  }
);

export const selectedMobileDeviceSelector = createSelector(
  filteredMobileDeviceModelsSelector,
  selectedMobileDeviceModelSelector,
  (filteredMobileDeviceModels, selectedMobileDeviceModel) => {
    const selectedMobileDevice = filteredMobileDeviceModels?.models?.find(
      device => device?.name === selectedMobileDeviceModel
    );

    return selectedMobileDevice;
  }
);

export const is5GCompatibleSelector = createSelector(
  isSimTypeEsimSelector,
  preselected5GAddonSelector,
  selectedMobileDeviceSelector,
  (isSimTypeEsim, preselected5GAddon, selectedMobileDevice) => {
    if (isEmpty(selectedMobileDevice)) {
      return null;
    }
    if (isSimTypeEsim) {
      if (!selectedMobileDevice.esim) {
        return null;
      } else if (selectedMobileDevice["5g"]) {
        return true;
      } else if (!selectedMobileDevice["5g"]) {
        if (preselected5GAddon) {
          return false;
        } else {
          return false;
        }
      }
    } else {
      if (selectedMobileDevice["5g"]) {
        return true;
      } else if (!selectedMobileDevice["5g"]) {
        if (preselected5GAddon) {
          return false;
        } else {
          return false;
        }
      }
    }
  }
);

export const enableNextButtonWithDeviceCheckerSelector = createSelector(
  isSimTypeEsimSelector,
  preselected5GAddonSelector,
  is5GAddonSelectedSelector,
  selectedMobileDeviceSelector,
  show5GDeviceCompatibilityCheckerSelector,
  (
    isSimTypeEsim,
    preselected5GAddon,
    is5GAddonSelected,
    selectedMobileDevice,
    enable5GDeviceCompatibilityChecker
  ) => {
    if (isSimTypeEsim) {
      if (isEmpty(selectedMobileDevice)) {
        return false;
      }
      if (!selectedMobileDevice.esim) {
        return false;
      } else if (selectedMobileDevice["5g"]) {
        return true;
      } else if (!selectedMobileDevice["5g"]) {
        if (preselected5GAddon) {
          return false;
        }
        return true;
      }
    } else {
      if (enable5GDeviceCompatibilityChecker) {
        if (!is5GAddonSelected) {
          return true;
        }
        if (is5GAddonSelected && isEmpty(selectedMobileDevice)) {
          return false;
        }
        if (!selectedMobileDevice["5g"]) {
          if (preselected5GAddon) {
            return false;
          }
          return true;
        } else if (selectedMobileDevice["5g"]) {
          return true;
        }
      } else {
        return true;
      }
    }
  }
);

export const hideComponentsWithDeviceCheckerSelector = createSelector(
  isSimTypeEsimSelector,
  preselected5GAddonSelector,
  selectedMobileDeviceSelector,
  (isSimTypeEsim, preselected5GAddon, selectedMobileDevice) => {
    if (isSimTypeEsim) {
      if (isEmpty(selectedMobileDevice)) {
        return true;
      }
      if (!selectedMobileDevice.esim) {
        return true;
      } else if (selectedMobileDevice.esim && selectedMobileDevice["5g"]) {
        return false;
      } else if (selectedMobileDevice.esim && !selectedMobileDevice["5g"]) {
        if (preselected5GAddon) {
          return true;
        }
        return false;
      }
    } else {
      return false;
    }
  }
);

export const displayIncomingInternationalComms = createSelector(
  selectAppSettings,
  appSettings => {
    const enableIncomingInternationalCallsandSMS = {
      incomingCalls: get(
        appSettings,
        "enable_incoming_international_calls",
        false
      ),
      incomingSMS: get(appSettings, "enable_incoming_international_sms", false)
    };
    return enableIncomingInternationalCallsandSMS;
  }
);

export const makeSelectAddons = () => addonsSelector;

export const makeSelectPreSelectedAddons = () => preSelectedAddonsSelector;

export const makeSelectDefaultPlanAddons = () => defaultAddonsSelector;

export const makeSelectSelectedAddons = () => additionalAddonsSelector;

export const makeSelectConfirmedAddon = () =>
  createSelector(additionalAddonsSelector, ([...addOns]) =>
    addOns
      .filter(addon => addon.selected && get(addon, "type") !== "sim")
      .map(addon => addon.label)
      .join(", ")
  );

export const makeSelectSimAddons = () =>
  createSelector(additionalAddonsSelector, ([...addOns]) =>
    addOns
      .filter(addon => addon.selected && get(addon, "type") === "sim")
      .map(addon => addon.label)
      .join()
  );

export const makeSelectSimCount = () =>
  createSelector(selfActivationSelector, self_activation =>
    get(self_activation, "sim_count")
  );

export const makeSelectPreSelectedOrderAddons = () =>
  preSelectedOrderAddonsSelector;

export const planDetailsSelector = createSelector(
  selfActivationSelector,
  localeSelector,
  planServiceSelector,
  (selfActivation, locale, { ...planData }) => {
    let payMonthlyItems = get(
      planData,
      "specs.pay_monthly_override",
      get(planData, "pay_monthly", false)
    );
    const sim_count = get(selfActivation, "sim_count");
    if (sim_count && payMonthlyItems) {
      const data = formatMessage(get(locale, "family_plan_sims"), {
        sim_count
      });
      planData["pay_monthly"] = [
        ...payMonthlyItems,
        { label: data, type: "sim", value: 0 }
      ];
    }
    return planData;
  }
);

export const makeSelectPlanDetails = () => planDetailsSelector;

export const makeSelectPlanFAQ = () =>
  createSelector(planServiceSelector, planService => get(planService, "faq"));

export const makeSelectPlanName = () =>
  createSelector(planServiceSelector, planService => get(planService, "name"));

export const makeSelectPlanTextName = () =>
  createSelector(planServiceSelector, planService =>
    get(planService, "textName")
  );

export const makeSelectSpecialNumberAllowed = () =>
  createSelector(planServiceSelector, planService =>
    get(planService, "special_number_allowed")
  );

export const makeSelectFamilyPlanSims = () =>
  createSelector(planDetailsSelector, ({ ...plan }) => {
    let payMonthly = get(plan, "pay_monthly", []);
    let simItems = filter(payMonthly, plan => get(plan, "type") === "sim")
      .reduce((acc, item) => {
        acc.push(item.label);
        return acc;
      }, [])
      .join(",");
    return simItems;
  });

export const makeSelectShow5GAddon = () =>
  createSelector(
    show5GAddonSelector,
    is5GBundleAddonSelectedSelector,
    planServiceSelector,
    (show5GAddon, is5GBundleSelected, planService) => {
      const supportedPlans = ["CirclesOne", "CirclesDataOnly"];
      return (
        show5GAddon &&
        !is5GBundleSelected &&
        supportedPlans.includes(planService?.name)
      );
    }
  );
export const makeSelectPayDevice = () => selectedDeviceSelector;

export const makeSelectPreselected5GAddon = () => preselected5GAddonSelector;

export const makeSelectIs5GAddonSelected = () => is5GAddonSelectedSelector;
export const makeSelectIs5GBundleAddonSelected = () =>
  is5GBundleAddonSelectedSelector;

export const makeSelectIs5GCompatible = () => is5GCompatibleSelector;

export const makeSelectSelectedMobileDeviceBrand = () =>
  selectedMobileDeviceBrandSelector;

export const makeSelectFilteredMobileDeviceModels = () =>
  filteredMobileDeviceModelsSelector;

export const makeSelectSelectedMobileDevice = () =>
  selectedMobileDeviceSelector;

export const makeSelectEnableNextButtonWithDeviceChecker = () =>
  enableNextButtonWithDeviceCheckerSelector;

export const makeSelectHideComponentsWithDeviceChecker = () =>
  hideComponentsWithDeviceCheckerSelector;

export const makeSelectIsSimTypeEsim = () => isSimTypeEsimSelector;

export const makeSelectEnableInternationalComms = () =>
  displayIncomingInternationalComms;

/**
 * 
 * Enterprise Automated REDUX START ########################
 */

export const makeSelectSimCountNewAndTransfer = () =>
  createSelector(
    simCountSelector,
    simCount => ({
      newSimCount: get(simCount, "newSimCount", 0),
      transferSimCount: get(simCount, "transferSimCount", 0),
      totalSimCount: get(simCount, "totalSimCount", 0)
    })
  );

export const makeSelectEnableThingsToKnowSection = () =>
  createSelector(
    enableThingsToKnowSelector,
    (enableThingsToKnow) => {
      return get(enableThingsToKnow, "enableThingsToKnow", false);
    }
  );

export const makeSelectEnableSimCountNextButton = () =>
  createSelector(
    enableSimCountNextBtnSelector,
    (enableSimCountNextBtn) => {
      return get(enableSimCountNextBtn, "enableSimCountNextBtn", false);
    }
  );

export const makeSelectAcraFileUploaded = () =>
  createSelector(
    isAcraFileUploadSelector,
    (isAcraFileUpload) => {
      return get(isAcraFileUpload, "isAcraFileUpload", false);
    }
  );

export const makeSelectCompanyDetails = () =>
  createSelector(
    companyDetailsSelector,
    companyDetails => ({
      company_name: get(companyDetails, "company_name", ""),
      uen: get(companyDetails, "uen", ""),
      company_address: get(companyDetails, "company_address", ""),
      file_name: get(companyDetails, "file_name", ""),
      status: get(companyDetails, "status", ""),
      error:{
        header: get(companyDetails, "error.header", ""),
        message: get(companyDetails, "error.message", "")
      }
    })
  );

export const makeSelectIsDeclaredCompanyDetails = () =>
  createSelector(
    isDeclaredCompanyDetailsSelector,
    (isDeclaredCompanyDetails) => {
      return get(isDeclaredCompanyDetails, "isDeclaredCompanyDetails", false);
    }
  );

export const makeSelectAccountPrimaryDetails = () => 
  createSelector(
    accountPrimaryDetailsSelector,
    accountPrimaryDetails => ({
      name: get(accountPrimaryDetails, "name", ""),
      mobile_no: get(accountPrimaryDetails, "mobile_no", ""),
      email: get(accountPrimaryDetails, "email", "")
    })
  );

export const makeSelectIsPrimaryDetailsCompleted = () =>
  createSelector(
    isPrimaryDetailsCompletedSelector,
    (isPrimaryDetailsCompleted) => {
      return get(isPrimaryDetailsCompleted, "isPrimaryDetailsCompleted", false);
    }
  );

  
export const makeSelectAccountSecondaryDetails = () => 
  createSelector(
    accountSecondaryDetailsSelector,
    accountSecondaryDetails => ({
      name: get(accountSecondaryDetails, "name", ""),
      mobile_no: get(accountSecondaryDetails, "mobile_no", ""),
      email: get(accountSecondaryDetails, "email", "")
    })
  );

export const makeSelectIsSecondaryDetailsCompleted = () =>
  createSelector(
    isSecondaryDetailsCompletedSelector,
    (isSecondaryDetailsCompleted) => {
      return get(isSecondaryDetailsCompleted, "isSecondaryDetailsCompleted", false);
    }
  );

export const makeSelectAccountBillingDetails = () => 
  createSelector(accountBillingDetailsSelector, accountBillingDetails =>
    get(accountBillingDetails, "billing_email", "")
  );

export const makeSelectBillingEmailCompleted = () =>
  createSelector(
    isBillingEmailCompletedSelector,
    (isBillingEmailCompleted) => {
      return get(isBillingEmailCompleted, "isBillingEmailCompleted", false);
    }
  );

export const makeSelectCompanyVerification = () =>
  createSelector(
    companyVerificationSelector,
    (companyVerification) => ({
      status: get(companyVerification, "status", "IDLE"),
      isLoading: get(companyVerification, "isLoading", false),
      isVerified: get(companyVerification, "isVerified", false),
      data: get(companyVerification, "data", null),
      error: get(companyVerification, "error", null)
    })
  );

export const makeSelectIsTermsAndConditionAccept = () =>
  createSelector(
    isTermsAndConditionAcceptSelector,
    (isTermsAndConditionAccept) => {
      return get(isTermsAndConditionAccept, "isTermsAndConditionAccept", false);
    }
  );

export const makeSelectAuditLogData = () =>
  createSelector(
    auditLogDataSelector,
    (auditLogData) => ({
      steps: get(auditLogData, "steps", []),
      workflow: get(auditLogData, "workflow", ""),
      request_id: get(auditLogData, "requestId", ""),
    })
  );
/**
 * 
 * Enterprise Automated REDUX END ########################
 */